import React from "react";
import { Rectangle } from "recharts";
import { ChartColors } from "atom/chart/ChartBaseConfig";

interface ChartDataItem {
  primaryValue: number;
  time: number;
  unit?: string;
  valueCalculation?: string;
}

interface Point {
  x: number;
  y: number;
  payload: {
    time: number;
    primaryValue: number;
    rawDataPoints: ChartDataItem[];
  };
}

export interface Series {
  props: {
    points: Point[];
  };
}

interface ChartCustomizedRectangleProps {
  formattedGraphicalItems: Series[];
  timeRange?: string;
}

const BLOOD_GLUCOSE_RANGES = [
  { min: 0, max: 70, color: ChartColors.Red, label: "Low" },
  { min: 70, max: 100, color: ChartColors.Green, label: "Target" },
  { min: 100, max: 140, color: ChartColors.Orange, label: "High" },
  { min: 140, max: 300, color: ChartColors.Red, label: "Very High" }
];

const getLowestValue = (rawDataPoints: ChartDataItem[] = []): number => {
  if (rawDataPoints.length === 0) {
    return 0;
  }

  return rawDataPoints.reduce(
    (min, value) => Math.min(min, value.primaryValue),
    Infinity
  );
};

const getHighestValue = (rawDataPoints: ChartDataItem[] = []): number => {
  if (rawDataPoints.length === 0) {
    return 0;
  }

  return rawDataPoints.reduce(
    (max, value) => Math.max(max, value.primaryValue),
    -Infinity
  );
};

export const ChartCustomizedRectangle: React.FC<
  ChartCustomizedRectangleProps
> = ({ formattedGraphicalItems, timeRange }) => {
  // Don't show rectangles for 1-day view
  if (timeRange === "1d") {
    return null;
  }

  const [firstSeries] = formattedGraphicalItems;
  const { points } = firstSeries.props;

  return points.map((point) => {
    const minMaxValues = {
      min: getLowestValue(point.payload.rawDataPoints),
      max: getHighestValue(point.payload.rawDataPoints)
    };

    const value = point.payload.primaryValue;

    // Find the range that contains this value
    const range = BLOOD_GLUCOSE_RANGES.find(
      (r) => value >= r.min && value < r.max
    );
    if (!range) {
      return null;
    }

    // Calculate the height based on the range
    const diff = minMaxValues.max - minMaxValues.min;
    const rangeHeight = diff !== 0 && diff < 20 ? 30 : diff; // difference smaller than 20 is too small to be noticed on the chart, so we set the height to 30

    // Calculate the y position to center the rectangle on the point
    const yPosition = point.y - rangeHeight / 2;

    return (
      <Rectangle
        key={`${point.payload.time}-range`}
        width={10}
        height={rangeHeight}
        x={point.x - 5}
        y={yPosition}
        fill="#E6E3DB"
        fillOpacity={1}
        radius={10}
        style={{ mixBlendMode: "darken" }}
      />
    );
  });
};

export default ChartCustomizedRectangle;
